import { z } from 'zod';
import { UUIDSchema } from '../ids.js';
export const AuditAction = z.enum([
    // employees
    'EMPLOYEE_INVITED',
    'EMPLOYEE_UPDATED',
    'EMPLOYEE_SUSPENDED',
    'EMPLOYEE_REACTIVATED',
    'EMPLOYEE_OFFBOARDED',
    'EMPLOYEE_BULK_IMPORTED',
    'CREDENTIAL_ISSUED',
    'CREDENTIAL_REVOKED',
    'CREDENTIAL_ENROLL_STARTED',
    'CREDENTIAL_ENROLL_COMPLETED',
    'CREDENTIAL_ENROLL_CANCELLED',
    // schedules / groups
    'SCHEDULE_CREATED',
    'SCHEDULE_UPDATED',
    'SCHEDULE_DELETED',
    'ACCESS_GROUP_CREATED',
    'ACCESS_GROUP_UPDATED',
    'ACCESS_GROUP_DELETED',
    'ACCESS_GROUP_MEMBER_ADDED',
    'ACCESS_GROUP_MEMBER_REMOVED',
    // Tranche AF: access-group drift detection + repair workflow.
    'ACCESS_GROUP_RESYNC_REQUESTED',
    'ACCESS_GROUP_MARK_RECONCILED',
    'ACCESS_GROUP_ORPHAN_CONTROLLER_OBJECT_DELETE_REQUESTED',
    // devices / lockdown
    'DEVICE_UNLOCKED',
    'DEVICE_STATE_CHANGED',
    'LOCKDOWN_ENGAGED',
    'LOCKDOWN_CLEARED',
    // tenants / scope
    'TENANT_CREATED',
    'TENANT_UPDATED',
    'TENANT_ARCHIVED',
    'BUILDING_RESOURCES_REFRESHED',
    'TENANT_RESOURCE_GRANTED',
    'TENANT_RESOURCE_REVOKED',
    'ACCESS_ZONE_CREATED',
    'ACCESS_ZONE_UPDATED',
    'ACCESS_ZONE_RESOURCES_REPLACED',
    'ACCESS_ZONE_ASSIGNED',
    'ACCESS_ZONE_UNASSIGNED',
    'MASTER_SCOPE_ENTERED',
    // Per-request use of explicit master tenant context. Emitted by
    // `requireTenant` when a master sends `x-tenant-context` on a
    // tenant-scoped route.
    'MASTER_SCOPE_USED',
    // Master repinned `site.default_tenant_id` (typically because the
    // previously-pinned tenant was archived or went missing). Includes
    // before/after IDs and a required operator reason.
    'SITE_DEFAULT_TENANT_CHANGED',
    // Master changed global credential policy toggles.
    'CREDENTIAL_POLICY_UPDATED',
    // Paid credential inventory and tenant responsibility tracking.
    'CREDENTIAL_INVENTORY_SYNCED',
    'CREDENTIALS_RECONCILED_FROM_CONTROLLER',
    'CONTROLLER_STATE_RECONCILED',
    'CREDENTIAL_INVENTORY_ALLOCATED',
    'CREDENTIAL_INVENTORY_RECLAIMED',
    'CREDENTIAL_INVENTORY_STATUS_CHANGED',
    // auth
    'LOGIN_SUCCEEDED',
    'LOGIN_FAILED',
    'LOGOUT',
    'MFA_ENROLLED',
    'PASSKEY_REGISTERED',
    'PASSKEY_REMOVED',
    'PASSWORD_RESET_REQUESTED',
    'PASSWORD_RESET_COMPLETED',
    // drift / reconciliation
    'CONTROLLER_DRIFT_DETECTED',
    // outbox operator overrides
    'OUTBOX_JOB_RETRIED',
    'OUTBOX_JOB_DELETED',
    // webhook operator actions
    'WEBHOOK_REGISTRATION_SUCCEEDED',
    'WEBHOOK_REGISTRATION_STALE_CLEANUP_FAILED',
    'WEBHOOK_REGISTRATION_FAILED',
    'WEBHOOK_SECRET_ROTATED',
    'WEBHOOK_SECRET_ROTATED_STALE_CLEANUP_FAILED',
    'WEBHOOK_SECRET_ROTATION_FAILED',
    'WEBHOOK_CONTROLLER_VERIFIED',
    'WEBHOOK_CONTROLLER_VERIFY_FAILED',
    'WEBHOOK_CONTROLLER_VERIFY_ERROR',
    // portal user lifecycle (users CRUD route)
    'USER_CREATED_WITH_INITIAL_PASSWORD',
    'USER_INVITED',
    'USER_INVITE_RESENT',
    'USER_UPDATED',
    'USER_SUSPENDED',
    'USER_REACTIVATED',
    'USER_ROLE_CHANGED',
    'USER_TENANT_CHANGED',
    'USER_SESSIONS_REVOKED',
    'USER_MFA_RESET',
    'USER_PASSWORD_RESET',
    'USER_DELETED',
    // bulk import
    'EMPLOYEE_BULK_DRY_RUN',
    // multi-family domain
    'BUILDING_CREATED',
    'BUILDING_UPDATED',
    'BUILDING_DELETED',
    'UNIT_CREATED',
    'UNIT_UPDATED',
    'UNIT_DELETED',
    'HOUSEHOLD_CREATED',
    'HOUSEHOLD_UPDATED',
    'HOUSEHOLD_VACATED',
    'HOUSEHOLD_DELETED',
    'RESIDENT_INVITED',
    'RESIDENT_UPDATED',
    'RESIDENT_REMOVED',
    'RESIDENTS_BULK_MOVE_IN',
    'AMENITY_CREATED',
    'AMENITY_UPDATED',
    'AMENITY_DELETED',
    'GUEST_PASS_ISSUED',
    'GUEST_PASS_REVOKED',
    'CALLBOX_ENTRY_CREATED',
    'CALLBOX_ENTRY_UPDATED',
    'CALLBOX_ENTRY_DELETED',
    'CALLBOX_GROUP_CREATED',
    'CALLBOX_GROUP_UPDATED',
    'CALLBOX_GROUP_DELETED',
    'CALLBOX_DEVICE_CREATED',
    'CALLBOX_DEVICE_UPDATED',
    'CALLBOX_DEVICE_RING_TARGET_UPDATED',
    'CALLBOX_DEVICE_DELETED',
    'CALLBOX_DEVICE_ASSIGNED',
    'CALLBOX_DEVICE_UNASSIGNED',
    'CALLBOX_DEVICE_SYNCED',
    'CALLBOX_DIRECTORY_MEMBER_CREATED',
    'CALLBOX_DIRECTORY_MEMBER_UPDATED',
    'CALLBOX_DIRECTORY_MEMBER_DELETED',
    'CALLBOX_TENANT_ENTRY_UPSERTED',
    // branding + terminology (master-only, global, not tenant-scoped)
    'MASTER_BRANDING_UPDATED',
    // intercom live call lifecycle (queued -> answered/missed -> ended)
    'INTERCOM_CALL_RECEIVED',
    'INTERCOM_CALL_ANSWERED',
    'INTERCOM_CALL_ENDED',
    'INTERCOM_CALL_MISSED',
    // vendor access lifecycle (time-bound third-party access grants)
    'VENDOR_ACCESS_GRANTED',
    'VENDOR_ACCESS_REVOKED',
    // Reserved for a follow-up scheduled sweep that transitions expired
    // grants to revoked automatically. Not emitted by T3 itself.
    'VENDOR_ACCESS_AUTO_REVOKED',
    // amenity reservation lifecycle
    'AMENITY_RESERVED',
    'AMENITY_RESERVATION_CANCELLED',
    // Reserved for a follow-up sweep that marks no-shows after the window
    // ends without a check-in. Not emitted by T4.
    'AMENITY_RESERVATION_NO_SHOW',
]);
export const AuditLogRow = z.object({
    id: UUIDSchema,
    tenant_id: UUIDSchema.nullable(),
    actor_user_id: UUIDSchema.nullable(),
    actor_session_id: UUIDSchema.nullable(),
    action: AuditAction,
    resource_type: z.string(),
    resource_id: z.string().nullable(),
    before_json: z.unknown().nullable(),
    after_json: z.unknown().nullable(),
    request_id: z.string().nullable(),
    ip: z.string().nullable(),
    user_agent: z.string().nullable(),
    unifi_request_id: z.string().nullable(),
    created_at: z.string().datetime(),
});
export const ListAuditQuery = z.object({
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    actor_id: UUIDSchema.optional(),
    action: AuditAction.optional(),
    /**
     * Tranche BF: server-side filter for an entity's audit trail. Detail
     * pages should pass both fields together (`resource_type` AND
     * `resource_id`) to scope the audit feed to one row's history; passing
     * `resource_type` alone is allowed (e.g. "all access-group changes")
     * but pagination cursors are not stable across filter changes.
     */
    resource_type: z.string().min(1).max(120).optional(),
    resource_id: z.string().min(1).max(120).optional(),
    cursor: z.string().max(2048).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
//# sourceMappingURL=audit.js.map